/**
 * useLeadsQuery.ts — fetch + keyset-pagination state for the Leads list.
 *
 * Owns the request lifecycle for `GET /api/leads`: the first page (a fresh
 * filter `load`), subsequent `loadMore` pages appended via the opaque
 * `next_cursor`, and the four first-class UI phases (loading / ready /
 * unauthorized / error). Filter state lives in the page; this hook only fetches
 * the `LeadsQuery` it is handed and remembers the last filter set so `loadMore`
 * can advance the cursor without the page re-deriving it.
 *
 * Race-safety mirrors HealthPage: a monotonic request id means only the LATEST
 * `load`/`loadMore` may commit, so a slow earlier response can never clobber a
 * newer one, and a response resolving after unmount is a no-op (no
 * AbortController — it is cross-realm-flaky under jsdom+undici).
 *
 * Failure posture (spec §Error Handling): a 401/403 → `unauthorized` (the clean
 * screen, never a broken page); the BFF's 502 "source unavailable" or any
 * network/5xx → `error` with an operator-readable message, never a crash or a
 * blank. A failed `loadMore` keeps the rows already on screen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient, LeadsQuery } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import type { LeadListItem } from '../../api/types';

export type LeadsPhase = 'loading' | 'ready' | 'unauthorized' | 'error';

export interface LeadsQueryState {
  items: LeadListItem[];
  nextCursor: string | null;
  approxTotal: number | null;
  generatedAt: string | null;
  phase: LeadsPhase;
  loadingMore: boolean;
  /** Set when a load failed (transport / 502 / 5xx); drives the error banner. */
  errored: boolean;
  /** Run a fresh first-page query (replaces the current rows). */
  load: (query: LeadsQuery) => Promise<void>;
  /** Append the next keyset page; no-op when there is no `next_cursor`. */
  loadMore: () => Promise<void>;
}

export const useLeadsQuery = (apiClient: ApiClient): LeadsQueryState => {
  const [items, setItems] = useState<LeadListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [approxTotal, setApproxTotal] = useState<number | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [phase, setPhase] = useState<LeadsPhase>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errored, setErrored] = useState(false);

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);
  // The active filter set (sans cursor), so loadMore can advance from it.
  const lastQueryRef = useRef<LeadsQuery>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(
    async (query: LeadsQuery, { append }: { append: boolean }) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const isCurrent = () => mountedRef.current && seq === requestSeq.current;
      if (append) setLoadingMore(true);
      else setPhase('loading');
      try {
        const envelope = await apiClient.getLeads(query);
        if (!isCurrent()) return;
        setItems((prev) => (append ? [...prev, ...envelope.items] : envelope.items));
        setNextCursor(envelope.next_cursor);
        setApproxTotal(envelope.approx_total);
        setGeneratedAt(envelope.generated_at);
        setErrored(false);
        setPhase('ready');
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setPhase('unauthorized');
          return;
        }
        // BFF 502 (source unavailable) / network / 5xx. Keep already-loaded
        // rows on an append failure; flag the error so the page can banner it.
        setErrored(true);
        if (append) {
          setPhase('ready');
        } else {
          setItems([]);
          setNextCursor(null);
          setPhase('error');
        }
      } finally {
        if (append && isCurrent()) setLoadingMore(false);
      }
    },
    [apiClient],
  );

  const load = useCallback(
    async (query: LeadsQuery) => {
      lastQueryRef.current = query;
      await run({ ...query, cursor: null }, { append: false });
    },
    [run],
  );

  const loadMore = useCallback(async () => {
    if (nextCursor == null) return;
    await run({ ...lastQueryRef.current, cursor: nextCursor }, { append: true });
  }, [nextCursor, run]);

  return {
    items,
    nextCursor,
    approxTotal,
    generatedAt,
    phase,
    loadingMore,
    errored,
    load,
    loadMore,
  };
};
